import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { engines, transmissions, bodyParts, type SparePart } from "@/mocks/products";

const ALL_PARTS: SparePart[] = [...engines, ...transmissions, ...bodyParts];

interface DbProduct {
  id: string;
  category: string;
  name: string;
  make: string | null;
  model: string | null;
  year_range: string | null;
  fitment: string | null;
  price: string | null;
  warranty: string | null;
  condition: string | null;
  stock: string | null;
  description: string | null;
  image_url: string | null;
}

function toSparePart(p: DbProduct): SparePart {
  return {
    id: p.id,
    name: p.name,
    category: p.category as SparePart["category"],
    make: p.make || "Universal",
    model: p.model || "",
    yearRange: p.year_range || "",
    fitment: p.fitment || "",
    price: p.price || "",
    warranty: p.warranty || "3 Month Used Parts Warranty",
    condition: p.condition || "Used",
    stock: p.stock || "In Stock",
    description: p.description || "",
    image_url: p.image_url || undefined,
  };
}

interface ProductState {
  product: SparePart | null;
  loading: boolean;
  error: string;
}

export function useProduct(id: string | undefined): ProductState {
  const [state, setState] = useState<ProductState>({
    product: null,
    loading: true,
    error: "",
  });

  useEffect(() => {
    if (!id) {
      setState({ product: null, loading: false, error: "" });
      return;
    }

    let active = true;
    setState((s) => ({ ...s, loading: true, error: "" }));

    supabase
      .from("products")
      .select("*")
      .is("deleted_at", null)
      .eq("id", id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          setState({ product: null, loading: false, error: error.message });
          return;
        }
        if (data) {
          setState({ product: toSparePart(data as DbProduct), loading: false, error: "" });
          return;
        }
        const fallback = ALL_PARTS.find((p) => p.id === id) || null;
        setState({ product: fallback, loading: false, error: "" });
      })
      .catch((err) => {
        if (!active) return;
        setState({
          product: null,
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load product",
        });
      });

    return () => {
      active = false;
    };
  }, [id]);

  return state;
}